// =====================================================================
// WorkspaceMediaPicker — grid + upload + delete dialog over workspace_media.
// =====================================================================
// Two responsibilities:
//   1. "Picker mode" (default): user selects one image; onPick(row, url) fires.
//   2. "Manage mode" (managerOnly=true): no selection callback; the dialog
//      acts as the standalone Workspace Media manager (used in Settings).
// =====================================================================

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Trash2, Upload, ImageIcon, AlertTriangle, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import {
  listWorkspaceMedia,
  signWorkspaceMediaUrls,
  signWorkspaceMediaUrl,
  uploadWorkspaceMedia,
  softDeleteWorkspaceMedia,
  restoreWorkspaceMedia,
  getWorkspaceMediaUsageBytes,
  formatBytes,
  WORKSPACE_MEDIA_ALLOWED_TYPES,
  WORKSPACE_MEDIA_MAX_BYTES,
  WorkspaceMediaCapError,
  type WorkspaceMediaRow,
} from "@/lib/workspaceMedia";
import { supabase } from "@/integrations/supabase/client";

const TRASH_WINDOW_MS = 24 * 60 * 60 * 1000;

const formatRemaining = (deletedAt: string): string => {
  const expires = new Date(deletedAt).getTime() + TRASH_WINDOW_MS;
  const ms = expires - Date.now();
  if (ms <= 0) return "expired";
  const hours = Math.floor(ms / (60 * 60 * 1000));
  const minutes = Math.floor((ms % (60 * 60 * 1000)) / (60 * 1000));
  if (hours > 0) return `${hours}h ${minutes}m left`;
  return `${minutes}m left`;
};

type Props = {
  workspaceId: string;
  /** Tier cap in bytes; `null` = unlimited. Drives meter + upload gate. */
  capBytes: number | null;
  /** Called when the user picks an existing or freshly uploaded image. */
  onPick?: (row: WorkspaceMediaRow, signedUrl: string) => void;
  /** When true, hide the "Pick" affordance — used by the Settings manager card. */
  managerOnly?: boolean;
};

export const WorkspaceMediaPanel = ({ workspaceId, capBytes, onPick, managerOnly = false }: Props) => {
  const [rows, setRows] = useState<WorkspaceMediaRow[]>([]);
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [usedBytes, setUsedBytes] = useState(0);
  const [query, setQuery] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [trashedRows, setTrashedRows] = useState<WorkspaceMediaRow[]>([]);
  const [trashUrls, setTrashUrls] = useState<Record<string, string>>({});

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [all, used] = await Promise.all([
        listWorkspaceMedia(workspaceId, { limit: 400, includeDeleted: true }),
        getWorkspaceMediaUsageBytes(workspaceId),
      ]);
      const cutoff = Date.now() - TRASH_WINDOW_MS;
      const live = all.filter((r) => !r.deleted_at);
      const trashed = all.filter(
        (r) => r.deleted_at && new Date(r.deleted_at).getTime() > cutoff,
      );
      setRows(live);
      setTrashedRows(trashed);
      setUsedBytes(used);
      const [signed, signedTrash] = await Promise.all([
        signWorkspaceMediaUrls(live.map((r) => r.storage_path)),
        signWorkspaceMediaUrls(trashed.map((r) => r.storage_path)),
      ]);
      setUrls(signed);
      setTrashUrls(signedTrash);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => { void refresh(); }, [refresh]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      (r.original_filename ?? "").toLowerCase().includes(q) ||
      (r.alt_text ?? "").toLowerCase().includes(q),
    );
  }, [rows, query]);

  const pct = capBytes ? Math.min(100, Math.round((usedBytes / capBytes) * 100)) : 0;
  const overSoft = capBytes !== null && pct >= 80;
  const overHard = capBytes !== null && usedBytes >= capBytes;

  const handleUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not signed in");
      for (const file of Array.from(files)) {
        try {
          const { row, deduplicated } = await uploadWorkspaceMedia({
            workspaceId,
            userId: user.id,
            file,
            capBytes,
          });
          if (deduplicated) {
            toast.message("Reused existing image", { description: row.original_filename ?? "Same content already in Workspace Media." });
          } else {
            toast.success("Uploaded to Workspace Media");
          }
        } catch (err) {
          if (err instanceof WorkspaceMediaCapError) {
            toast.error("Storage cap reached", { description: err.message });
            break;
          }
          toast.error((err as Error).message);
        }
      }
      await refresh();
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Move this image to the 24h trash? It will be permanently deleted after a day.")) return;
    try {
      await softDeleteWorkspaceMedia(id);
      toast.success("Image removed");
      await refresh();
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const handlePick = async (row: WorkspaceMediaRow) => {
    if (!onPick) return;
    try {
      const signed = urls[row.storage_path] ?? (await signWorkspaceMediaUrl(row.storage_path));
      onPick(row, signed);
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const handleRestore = async (id: string) => {
    try {
      await restoreWorkspaceMedia(id);
      toast.success("Image restored");
      await refresh();
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  return (
    <div className="space-y-4">
      {capBytes !== null && (
        <div className="space-y-1">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              {formatBytes(usedBytes)} of {formatBytes(capBytes)} used
            </span>
            <span>{pct}%</span>
          </div>
          <Progress value={pct} className={overHard ? "[&>div]:bg-destructive" : overSoft ? "[&>div]:bg-amber-500" : ""} />
          {overHard && (
            <p className="flex items-center gap-1 text-xs text-destructive">
              <AlertTriangle className="h-3 w-3" /> Cap reached. Delete images or upgrade your plan to upload more.
            </p>
          )}
          {!overHard && overSoft && (
            <p className="text-xs text-amber-600 dark:text-amber-500">
              You're nearing your storage cap.
            </p>
          )}
        </div>
      )}

      <div className="flex items-center gap-2">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by filename or alt text…"
          className="h-9"
        />
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading || overHard}
        >
          {uploading ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Upload className="mr-1 h-4 w-4" />}
          Upload
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={Array.from(WORKSPACE_MEDIA_ALLOWED_TYPES).join(",")}
          className="hidden"
          onChange={(e) => handleUpload(e.target.files)}
        />
      </div>
      <p className="text-xs text-muted-foreground">
        PNG or JPEG, up to {WORKSPACE_MEDIA_MAX_BYTES / (1024 * 1024)} MB each. Images are auto-resized to 1920px and deduplicated by content.
      </p>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-md border border-dashed py-10 text-center text-sm text-muted-foreground">
          <ImageIcon className="mx-auto mb-2 h-6 w-6 opacity-50" />
          {rows.length === 0 ? "No images uploaded yet." : "No matches."}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
          {filtered.map((row) => {
            const url = urls[row.storage_path];
            const onDragStart = (e: React.DragEvent<HTMLDivElement>) => {
              if (!url) { e.preventDefault(); return; }
              e.dataTransfer.effectAllowed = "copy";
              e.dataTransfer.setData(
                "application/x-excerptus-media",
                JSON.stringify({ mediaId: row.id, alt: row.alt_text ?? "" }),
              );
              // Fallback so a drop outside our editor at least produces a URL.
              e.dataTransfer.setData("text/uri-list", url);
              e.dataTransfer.setData("text/plain", url);
            };
            return (
              <div
                key={row.id}
                className="group relative overflow-hidden rounded-md border bg-muted/30"
                draggable={Boolean(url)}
                onDragStart={onDragStart}
                title="Drag into the Draft Canvas to insert"
              >
                <button
                  type="button"
                  className="block aspect-square w-full"
                  onClick={() => !managerOnly && handlePick(row)}
                  disabled={managerOnly}
                  title={row.original_filename ?? ""}
                >
                  {url ? (
                    <img src={url} alt={row.alt_text ?? ""} className="h-full w-full object-cover pointer-events-none" draggable={false} />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-xs text-muted-foreground">
                      …
                    </div>
                  )}
                </button>
                <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-1 bg-background/85 px-1.5 py-1 text-[10px] backdrop-blur">
                  <span className="truncate" title={row.original_filename ?? ""}>
                    {row.original_filename ?? "image"}
                  </span>
                  <span className="text-muted-foreground">{formatBytes(row.bytes)}</span>
                </div>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="absolute right-1 top-1 h-6 w-6 opacity-0 transition-opacity group-hover:opacity-100"
                  onClick={(e) => { e.stopPropagation(); void handleDelete(row.id); }}
                  aria-label="Delete image"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {trashedRows.length > 0 && (
        <details className="rounded-md border bg-muted/30 p-3">
          <summary className="cursor-pointer text-sm font-medium">
            Recently deleted ({trashedRows.length}){" "}
            <span className="font-normal text-muted-foreground">— restorable for 24 hours</span>
          </summary>
          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
            {trashedRows.map((row) => {
              const url = trashUrls[row.storage_path];
              return (
                <div key={row.id} className="group relative overflow-hidden rounded-md border bg-background opacity-70">
                  <div className="aspect-square w-full">
                    {url ? (
                      <img src={url} alt={row.alt_text ?? ""} className="h-full w-full object-cover grayscale" />
                    ) : null}
                  </div>
                  <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-1 bg-background/85 px-1.5 py-1 text-[10px] backdrop-blur">
                    <span className="truncate" title={row.original_filename ?? ""}>
                      {row.original_filename ?? "image"}
                    </span>
                    <span className="text-muted-foreground">{row.deleted_at ? formatRemaining(row.deleted_at) : ""}</span>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    className="absolute right-1 top-1 h-7 gap-1 px-2 text-[10px]"
                    onClick={() => void handleRestore(row.id)}
                  >
                    <RotateCcw className="h-3 w-3" /> Restore
                  </Button>
                </div>
              );
            })}
          </div>
        </details>
      )}
    </div>
  );
};
